import { AssignTo } from "@coworker/apprestructured/src/task-management/components/AssignTo/AssignTo";
import {
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  useMyTeam,
  useTeamsByStore,
} from "@coworker/app/src/core/hooks/useStoreTeams";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";
import {
  clean,
  makeMatcher,
} from "@coworker/components/src/helpers/string.helpers";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateStringType } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";

export type AssignToCreatorFragmentConfigProps = EmptyObject;

type AssignToCreatorFragment = {
  configProps: AssignToCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AssignToCreatorFragment({
  commonProps,
  ref,
}: AssignToCreatorFragment) {
  const { blockInput, task, onTaskChange, showValidation } = commonProps;
  const assignedUserId =
    validateStringType(getByPath(task, "assigned_user_id")) ?? "";
  const assignedTeamId =
    validateStringType(getByPath(task, "assigned_team_id")) ?? "";

  const [searchQuery, setSearchQuery] = useState("");

  const storeId = useStoreId();

  const { data: teams, isLoading: isTeamsLoading } = useTeamsByStore(
    storeId || ""
  );

  const { data: activeUsers, isLoading: isUsersLoading } =
    useActiveStoreUsers();
  const myTeam = useMyTeam();

  const assignableTeams = useMemo(() => {
    return teams?.filter((team) => team.status === "ACTIVE");
  }, [teams]);

  const sortedTeams = useMemo(() => {
    return assignableTeams?.sort((teamA, teamB) =>
      teamA.name.localeCompare(teamB.name)
    );
  }, [assignableTeams]);

  const matchAll = useMemo(() => {
    return makeMatcher(searchQuery, true);
  }, [searchQuery]);

  const matcher = useCallback(
    (query: string) => {
      return !matchAll || matchAll.test(clean(query));
    },
    [matchAll]
  );

  const matchingTeams = useMemo(() => {
    return sortedTeams?.filter((team) => matcher(team.name));
  }, [matcher, sortedTeams]);

  const matchingUsers = useMemo(() => {
    return (activeUsers || []).filter((user) => matcher(user.fullName));
  }, [activeUsers, matcher]);

  const description = useMemo(() => {
    if (isLoading(assignedUserId) || isLoading(assignedTeamId)) {
      return "";
    }

    return assignedUserId
      ? (activeUsers || []).find((user) => user.uid === assignedUserId)
          ?.fullName || ""
      : assignedTeamId
        ? teams?.find((team) => team.id === assignedTeamId)?.name || ""
        : "";
  }, [activeUsers, assignedTeamId, assignedUserId, teams]);

  const handleConfirmClick = useCallback(
    ({
      assignedTeamId,
      assignedUserId,
    }: {
      assignedTeamId: string | null;
      assignedUserId: string | null;
    }) => {
      onTaskChange({
        assigned_user_id: assignedUserId,
        assigned_team_id: assignedTeamId,
      });
    },
    [onTaskChange]
  );

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const hasAssignableTeams =
    Array.isArray(assignableTeams) && assignableTeams.length > 0;

  // Valid only if assigned_team_id is found in teams list
  const isTeamFound =
    !isTeamsLoading &&
    hasAssignableTeams &&
    !!assignableTeams?.some((team) => team.id === assignedTeamId);

  const isUserIdFound =
    !isUsersLoading &&
    (activeUsers || []).some((user) => user.uid === assignedUserId);

  const isValidAssignedTeamId =
    !isLoading(assignedTeamId) && !!assignedTeamId && isTeamFound;
  const isValidAssignedUserId =
    !isLoading(assignedUserId) && !!assignedUserId && isUserIdFound;

  const valid = assignedUserId
    ? isValidAssignedTeamId && isValidAssignedUserId
    : isValidAssignedTeamId;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  return (
    <AssignTo
      assignedTeamId={isLoading(assignedTeamId) ? null : assignedTeamId}
      assignedUserId={isLoading(assignedUserId) ? null : assignedUserId}
      disabled={
        blockInput || isLoading(assignedTeamId) || isLoading(assignedUserId)
      }
      description={description}
      searchQuery={searchQuery}
      matchingTeams={matchingTeams || []}
      isTeamsLoading={isTeamsLoading}
      myTeamId={myTeam?.id || ""}
      matchingUsers={matchingUsers || []}
      isUsersLoading={isUsersLoading}
      teams={assignableTeams || []}
      isError={!valid && showValidation}
      onConfirmClick={handleConfirmClick}
      onSearchQueryChange={handleSearchQueryChange}
    />
  );
}

export default AssignToCreatorFragment;
